/**
 * IncomeSources — displays the list of income sources and the add-source form.
 * Owns all form state locally; calls updateState to persist additions/removals.
 */
import React, { useState } from 'react';
import { fc, INCOME_TYPES, FREQ_LABELS, srcMonthly, sourcesToMonthly } from '../../utils';

export function IncomeSources({ incomeSources, srcTotalMonthly, updateState, showToast }) {
  const [open, setOpen] = useState(false);
  const [srcName, setSrcName] = useState('');
  const [srcType, setSrcType] = useState('job');
  const [srcAmt, setSrcAmt] = useState('');
  const [srcFreq, setSrcFreq] = useState('biweekly');
  const [srcMode, setSrcMode] = useState('percheck');
  const [srcFirstPay, setSrcFirstPay] = useState('');

  const srcPreview =
    srcMode === 'monthly' ? parseFloat(srcAmt) || 0 : srcMonthly({ amt: srcAmt, freq: srcFreq });

  const handleAdd = () => {
    const parsedAmt = parseFloat(srcAmt);
    if (!srcAmt || isNaN(parsedAmt) || parsedAmt <= 0) { showToast('Enter a valid amount greater than $0'); return; }
    const label = INCOME_TYPES.find((t) => t.id === srcType)?.label || 'Income';
    const name = srcName.trim() || label;
    const freq = srcMode === 'monthly' ? 'monthly' : srcFreq;
    const isPeriodicFreq = ['biweekly', 'weekly'].includes(srcMode === 'monthly' ? '' : srcFreq);
    const firstPayMs = isPeriodicFreq && srcFirstPay
      ? new Date(srcFirstPay + 'T12:00:00').getTime()
      : undefined;
    const newSrc = {
      id: 'inc_' + Date.now(), name, type: srcType, amt: parsedAmt, freq,
      ...(firstPayMs ? { firstPayMs } : {}),
    };
    const next = [...incomeSources, newSrc];
    updateState({ incomeSources: next, monthlyIncome: Math.round(sourcesToMonthly(next) * 100) / 100 });
    setSrcName(''); setSrcAmt(''); setSrcFirstPay(''); setOpen(false);
    showToast('Income source added!');
  };

  const handleRemove = (id) => {
    const next = incomeSources.filter((s) => s.id !== id);
    updateState({ incomeSources: next, monthlyIncome: Math.round(sourcesToMonthly(next) * 100) / 100 });
    showToast('Removed');
  };

  return (
    <div className="card cp" style={{ marginBottom: 12 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: incomeSources.length || open ? 10 : 0 }}>
        <div>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--mut)', textTransform: 'uppercase', letterSpacing: '.5px' }}>
            💼 Income Sources
          </div>
          {srcTotalMonthly > 0 && (
            <div style={{ fontSize: 10, color: 'var(--mut)', marginTop: 1 }}>
              Total: <strong style={{ color: 'var(--grn)' }}>{fc(srcTotalMonthly)}/mo · {fc(srcTotalMonthly * 12)}/yr</strong>
            </div>
          )}
        </div>
        <button onClick={() => setOpen((o) => !o)} style={{ fontSize: 11, fontWeight: 700, padding: '4px 10px', borderRadius: 8, border: '1.5px solid var(--bdr)', background: 'var(--surf2)', color: 'var(--txt)', cursor: 'pointer' }}>
          {open ? '✕ Cancel' : '+ Add'}
        </button>
      </div>

      {incomeSources.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 1, marginBottom: open ? 10 : 0 }}>
          {incomeSources.map((src) => {
            const mo = srcMonthly(src);
            const ti = INCOME_TYPES.find((t) => t.id === src.type);
            return (
              <div key={src.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 0', borderBottom: '1px solid var(--bdr)' }}>
                <span style={{ fontSize: 15 }}>{ti?.em}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 12, fontWeight: 700 }}>{src.name}</div>
                  <div style={{ fontSize: 10, color: 'var(--mut)' }}>
                    {fc(src.amt)} {FREQ_LABELS[src.freq]?.toLowerCase()} · <strong style={{ color: 'var(--grn)' }}>{fc(mo)}/mo</strong> · {fc(mo * 12)}/yr
                    {src.firstPayMs && <span style={{ marginLeft: 6 }}>· next {new Date(src.firstPayMs).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                  </div>
                </div>
                <button className="del" onClick={() => handleRemove(src.id)}>✕</button>
              </div>
            );
          })}
        </div>
      )}

      {open && (
        <div style={{ borderTop: incomeSources.length ? '1px solid var(--bdr)' : 'none', paddingTop: incomeSources.length ? 10 : 0 }}>
          <div className="lf">
            <label style={{ fontSize: 11 }}>Type</label>
            <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
              {INCOME_TYPES.map((t) => (
                <button key={t.id} onClick={() => setSrcType(t.id)} style={{ padding: '4px 9px', borderRadius: 7, fontSize: 11, fontWeight: 600, cursor: 'pointer', background: srcType === t.id ? 'var(--ylw)' : 'var(--bg)', color: srcType === t.id ? '#000' : 'var(--txt)', border: srcType === t.id ? '1.5px solid var(--ylw)' : '1.5px solid var(--bdr)' }}>
                  {t.em} {t.label}
                </button>
              ))}
            </div>
          </div>
          <div className="lf">
            <label style={{ fontSize: 11 }}>Name <span style={{ color: 'var(--mut)', fontWeight: 400 }}>(optional)</span></label>
            <input className="ob-inp" placeholder={INCOME_TYPES.find((t) => t.id === srcType)?.label} value={srcName} onChange={(e) => setSrcName(e.target.value)} />
          </div>
          <div className="lf">
            <label style={{ fontSize: 11 }}>How do you want to enter this?</label>
            <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1.5px solid var(--bdr)', width: 'fit-content' }}>
              {[['percheck', 'Per paycheck'], ['monthly', 'Monthly total']].map(([id, lbl]) => (
                <button key={id} onClick={() => setSrcMode(id)} style={{ padding: '5px 12px', fontSize: 11, fontWeight: 600, cursor: 'pointer', border: 'none', background: srcMode === id ? 'var(--ylw)' : 'var(--surf2)', color: srcMode === id ? '#000' : 'var(--mut)' }}>
                  {lbl}
                </button>
              ))}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
            <div style={{ flex: 1, minWidth: 110 }}>
              <label style={{ fontSize: 11, color: 'var(--mut)', display: 'block', marginBottom: 4 }}>
                {srcMode === 'monthly' ? 'Monthly amount' : 'Amount per paycheck'}
              </label>
              <div className="ob-amt-wrap">
                <span>$</span>
                <input type="number" placeholder="0" value={srcAmt} onChange={(e) => setSrcAmt(e.target.value)} min="0" />
              </div>
            </div>
            {srcMode === 'percheck' && (
              <div style={{ flex: 1, minWidth: 130 }}>
                <label style={{ fontSize: 11, color: 'var(--mut)', display: 'block', marginBottom: 4 }}>Paid</label>
                <select value={srcFreq} onChange={(e) => setSrcFreq(e.target.value)} className="ob-sel">
                  {Object.entries(FREQ_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                </select>
              </div>
            )}
          </div>
          {srcMode === 'percheck' && ['biweekly', 'weekly'].includes(srcFreq) && (
            <div className="lf">
              <label style={{ fontSize: 11 }}>
                Next payday <span style={{ color: 'var(--mut)', fontWeight: 400 }}>(optional — improves Bank projection accuracy)</span>
              </label>
              <input
                type="date"
                value={srcFirstPay}
                onChange={(e) => setSrcFirstPay(e.target.value)}
                style={{ padding: '6px 10px', borderRadius: 8, border: '1.5px solid var(--bdr)', background: 'var(--surf2)', color: 'var(--txt)', fontSize: 12, fontFamily: 'inherit' }}
              />
            </div>
          )}
          {parseFloat(srcAmt) > 0 && (
            <div style={{ padding: '8px 10px', background: 'var(--bg)', border: '1px solid var(--bdr)', borderRadius: 8, marginBottom: 8 }}>
              <div style={{ display: 'grid', gridTemplateColumns: srcMode === 'percheck' ? '1fr 1fr 1fr' : '1fr 1fr', gap: 8 }}>
                {srcMode === 'percheck' && (
                  <div>
                    <div style={{ fontSize: 9, color: 'var(--mut)' }}>Per paycheck</div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>{fc(parseFloat(srcAmt))}</div>
                  </div>
                )}
                <div>
                  <div style={{ fontSize: 9, color: 'var(--mut)' }}>Per month</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--grn)' }}>{fc(srcPreview)}</div>
                  {srcMode === 'percheck' && srcFreq !== 'monthly' && (
                    <div style={{ fontSize: 9, color: 'var(--mut)' }}>
                      {fc(parseFloat(srcAmt))} × {srcFreq === 'weekly' ? '52÷12' : srcFreq === 'biweekly' ? '26÷12' : srcFreq === 'semimonthly' ? '2' : '1÷12'}
                    </div>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: 9, color: 'var(--mut)' }}>Per year</div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{fc(srcPreview * 12)}</div>
                </div>
              </div>
            </div>
          )}
          <button className="vad" onClick={handleAdd}>+ Add Source</button>
        </div>
      )}

      {!incomeSources.length && !open && (
        <div className="em">No income sources set up — click + Add to get started</div>
      )}
    </div>
  );
}
